import { useState, useEffect } from "react";
import PaymentMethodList from "./paymentMethodList";
import { PaymentInstrument } from "./types";
import PaymentInstrumentForm from "./CreatePaymentInstrumentForm";
import useAxios from "@/hooks/useAxios";
import { config } from "@/config";
import MessageGroup from "@/components/MessageGroup";
import usePaymentInstrumentForm from "./usePaymentInstrumentForm";
import LoadingModal from "@/components/LoadingModal";
import { toast } from "@/components/Toast";

export default function PaymentInstruments() {
    const [showForm, setShowForm] = useState(false);
    const [storedPaymentInstruments, setStoredPaymentInstruments] = useState<PaymentInstrument[]>([]);
    const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
    const axios = useAxios();
    const { processing, errorMessage, onClear, submitPaymentInstrument }
        = usePaymentInstrumentForm(onSuccess);

    useEffect(() => {
        fetchStoredPaymentInstruments();
    }, []);

    async function onSuccess() {
        try {
            setShowForm(false);
            await fetchStoredPaymentInstruments();
        } catch (err) {
            console.error(err);
        }
    }

    async function fetchStoredPaymentInstruments() {
        try {
            const res = await axios.get(`${config.backendUrl}${config.paymentInstrumentsUrl}`);
            setStoredPaymentInstruments(res.data);
        } catch (err) {
            console.error(err);
        }
    }

    async function handleSetDefault(paymentInstrument: PaymentInstrument) {
        if (paymentInstrument.isDefault) return;
        try {
            setSettingDefaultId(paymentInstrument.id);
            await axios.patch(`${config.backendUrl}${config.paymentInstrumentsUrl}/${paymentInstrument.id}/default`, {});
            await fetchStoredPaymentInstruments();
            toast.success("Default bank account updated");
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Failed to set default bank account");
        } finally {
            setSettingDefaultId(null);
        }
    }

    return (
        <div className="relative">
            <h2 className="text-lg font-semibold mb-4">Payment method</h2>

            <PaymentMethodList
                paymentInstruments={storedPaymentInstruments}
                onSetDefault={handleSetDefault}
                settingDefaultId={settingDefaultId}
            />

            {!showForm && (
                <button onClick={() => setShowForm(true)} className="text-sm text-blue-600 hover:underline">
                    + Add a new payment method
                </button>
            )}

            {showForm && (
                <div>
                    <PaymentInstrumentForm
                        onSubmit={submitPaymentInstrument}
                        onClear={onClear}
                        onCancel={() => setShowForm(false)}
                    />
                    <MessageGroup errorMessage={errorMessage} successMessage={null} />
                </div>
            )}
            <LoadingModal loading={processing || !!settingDefaultId} />
        </div>
    );
}
